// src/components/ProjectManager.js
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';

import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Menu,
  CircularProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarField from './StarField';

// -------------------------------------------------------------------
// 1. Move default files outside the component so they're created once
// -------------------------------------------------------------------
const DEFAULT_FILES = {
  "React Native (expo)": [
        { 
          name: "App.js", 
          content: `// This is the main entry point for your React Native app.
// Use this file to set up your app's navigation and main components.
import React from 'react';
import { Text, View } from 'react-native';
export default function App() {
  return (
    <View>
      <Text>Welcome to your React Native app!</Text>
    </View>
  );
}`
        }
      ],
      "Python": [
        { 
          name: "main.py", 
          content: `# This is the main script for your Python project.
# Use this file to define the main logic and entry point of your application.
def main():
    print("Welcome to your Python project!")
if __name__ == "__main__":
    main()`
        }
      ],
      "JavaScript": [
        { 
          name: "index.js", 
          content: `// This is the main entry point for your JavaScript project.
// Use this file to initialize your application and define its main logic.
console.log("Welcome to your JavaScript project!");`
        }
      ],
      "C++": [
        { 
          name: "main.cpp", 
          content: `// This is the main entry point for your C++ project.
// Use this file to define the main logic and entry point of your application.
#include <iostream>
int main() {
    std::cout << "Welcome to your C++ project!" << std::endl;
    return 0;
}`
        }
      ],
      "Java": [
        { 
          name: "Main.java", 
          content: `// This is the main entry point for your Java project.
// Use this file to define the main logic and entry point of your application.
public class Main {
    public static void main(String[] args) {
        System.out.println("Welcome to your Java project!");
    }
}`
        }
      ]
    };

// ----------------------------------------------------
// Utility function to determine MIME type by extension
// ----------------------------------------------------
function getMimeType(fileName) {
  const extension = fileName.split('.').pop();
  switch (extension) {
    case 'js':
      return 'application/javascript';
    case 'py':
      return 'text/x-python';
    case 'cpp':
      return 'text/x-c++src';
    case 'java':
      return 'text/x-java-source';
    default:
      return 'text/plain';
  }
}

const ProjectManager = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);

  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectType, setNewProjectType] = useState('React Native (expo)');
  
  const [menuAnchor, setMenuAnchor] = useState({ anchor: null, projectId: null });
  const [isLoading, setIsLoading] = useState(false);

  // ------------------------------------------------------
  // 2. Fetch projects once when currentUser changes
  // ------------------------------------------------------
  const fetchProjects = useCallback(async () => {
    if (!currentUser) {
      setProjects([]);
      return;
    }
    try {
      const q = query(
        collection(db, 'projects'),
        where('owner', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const projList = [];
      querySnapshot.forEach((document) => {
        projList.push({ id: document.id, ...document.data() });
      });
      setProjects(projList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ------------------------------------------------------
  // 3. Optional: Remove global keydown listener
  //    Instead, let user explicitly click "Create"
  // ------------------------------------------------------
  // If you really want to handle 'Enter' globally, keep it:
  // useEffect(() => {
  //   const handleKeyDown = (event) => {
  //     if (
  //       event.key === 'Enter' &&
  //       newProjectName.trim() &&
  //       newProjectDescription.trim()
  //     ) {
  //       handleCreateProject();
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     document.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [newProjectName, newProjectDescription]);

  // ------------------------------------------------------
  // 4. Handlers for menu open/close
  // ------------------------------------------------------
  const handleOpenMenu = (event, projectId) => {
    setMenuAnchor({ anchor: event.currentTarget, projectId });
  };
  const handleCloseMenu = () => {
    setMenuAnchor({ anchor: null, projectId: null });
  };

  // ------------------------------------------------------
  // 5. Delete a project
  // ------------------------------------------------------
  const handleDeleteProject = async (projectId) => {
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (error) {
      console.error('Error deleting project: ', error);
    }
    handleCloseMenu();
  };

  // ------------------------------------------------------
  // 6. Create a project
  // ------------------------------------------------------
  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !newProjectDescription.trim()) return;
    setIsLoading(true);

    const newProject = {
      owner: currentUser.uid,
      name: newProjectName.trim(),
      description: newProjectDescription.trim(),
      type: newProjectType,
      files: DEFAULT_FILES[newProjectType] || [],
      dependencies: []
    };

    try {
      // Add main project doc
      const docRef = await addDoc(collection(db, 'projects'), newProject);

      // Upload each default file to Firebase
      for (const file of newProject.files) {
        await saveSingleFileToFirebase(file, docRef.id);
      }

      // Reset fields
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectType('React Native (expo)');

      // Navigate to dashboard with new project data
      navigate('/dashboard', {
        state: {
          projectId: docRef.id,
          files: newProject.files,
          dependencies: newProject.dependencies,
          projectName: newProject.name,
          projectType: newProject.type,
          description: newProject.description,
          init: true
        }
      });
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------------------
  // 7. Save a single file to Firebase
  // ------------------------------------------------------
  const saveSingleFileToFirebase = async (file, projectId) => {
    try {
      const mimeType = getMimeType(file.name);
      const fileBlob = new Blob([file.content], { type: mimeType });

      const storageRef = ref(
        storage,
        `users/${currentUser.uid}/projects/${projectId}/files/${file.name}`
      );

      await uploadBytes(storageRef, fileBlob);
      const fileURL = await getDownloadURL(storageRef);

      // Save file metadata to a sub-collection or doc
      const fileDocRef = doc(
        db,
        `users/${currentUser.uid}/projects/${projectId}/files`,
        file.name
      );
      await setDoc(fileDocRef, {
        name: file.name,
        url: fileURL,
        content: file.content // optional
      });

      console.log('File saved successfully:', file.name);
    } catch (error) {
      console.error('Error saving file:', error);
    }
  };

  // ------------------------------------------------------
  // 8. Load an existing project
  // ------------------------------------------------------
  const handleLoadProject = (project) => {
    navigate('/dashboard', {
      state: {
        projectId: project.id,
        files: project.files || [],
        dependencies: project.dependencies || [],
        projectName: project.name,
        projectType: project.type,
        description: project.description || ''
      }
    });
  };

  // ------------------------------------------------------
  // 9. Render
  // ------------------------------------------------------
  return (
    <Box sx={{ position: 'relative', p: 2, color: '#ffffff' }}>
      <StarField />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Top Navigation / Profile Buttons */}
        <Box sx={{ p: 2, color: '#ffffff' }}>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              borderRadius: '0px',
              backgroundColor: 'transparent',
              border: 'none'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 'bold', mr: 2 }}>
              Chlo
            </Typography>
            <Button
              onClick={() => navigate('/profile')}
              sx={{
                color: '#ffffff',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              Profile
            </Button>
            <Button
              onClick={() => navigate('/about')}
              sx={{
                color: '#ffffff',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              About
            </Button>
          </Box>

          <Box
            sx={{
              mt: 20,
              mb: 30,
              px: 15
            }}
          >
            <TextField
              label="Describe your project..."
              variant="outlined"
              fullWidth
              multiline
              rows={8}
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              margin="normal"
              InputProps={{
                sx: {
                  color: '#ffffff',
                  backgroundColor: '#121212',
                  borderRadius: '8px',
                  '&.MuiOutlinedInput-root fieldset': { borderColor: '#444' },
                  '&:hover fieldset': { borderColor: '#ffffff !important' },
                  '&.Mui-focused fieldset': { borderColor: '#ffffff !important' }
                }
              }}
              InputLabelProps={{ style: { color: '#ffffff' } }}
              sx={{
                width: '100%',
                mb: 2
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              mt={2}
              sx={{
                width: '50%',
                margin: '0 auto'
              }}
            >
              <TextField
                label="Project Name"
                variant="outlined"
                fullWidth
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                InputProps={{
                  sx: {
                    color: '#ffffff',
                    backgroundColor: '#121212',
                    borderRadius: '8px',
                    '&.MuiOutlinedInput-root fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#ffffff !important' },
                    '&.Mui-focused fieldset': { borderColor: '#ffffff !important' }
                  }
                }}
                InputLabelProps={{ style: { color: '#ffffff' } }}
              />

              <FormControl
                variant="outlined"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    color: '#ffffff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#ffffff !important' },
                    '&.Mui-focused fieldset': { borderColor: '#ffffff' }
                  }
                }}
              >
                <InputLabel sx={{ color: '#ffffff !important' }}>
                  Project Type
                </InputLabel>
                <Select
                  value={newProjectType}
                  onChange={(e) => setNewProjectType(e.target.value)}
                  label="Project Type"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#2a2a2a',
                        color: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: 'none',
                        border: '1px solid #333'
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-icon': {
                      color: '#ffffff'
                    }
                  }}
                >
                  <MenuItem value="React Native (expo)">React Native (expo)</MenuItem>
                  <MenuItem value="Python">Python</MenuItem>
                  <MenuItem value="JavaScript">JavaScript</MenuItem>
                  <MenuItem value="C++">C++</MenuItem>
                  <MenuItem value="Java">Java</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleCreateProject}
                disabled={
                  !newProjectName.trim() || !newProjectDescription.trim() || isLoading
                }
                sx={{
                  backgroundColor: '#333333',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#555555' },
                  '&.Mui-disabled': {
                    color: '#aaaaaa',
                    backgroundColor: '#111111'
                  },
                  width: '300px',
                  height: '50px',
                  borderRadius: '30px',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isLoading ? (
                  <>
                    Creating...
                  </>
                ) : (
                  <>
                    Create <ArrowForwardIcon sx={{ ml: 1 }} />
                  </>
                )}
              </Button>
            </Box>
          </Box>

          {/* Projects List */}
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Projects
          </Typography>
          {projects.length === 0 ? (
            <Typography>No projects found. Create one above!</Typography>
          ) : (
            <List
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2
              }}
            >
              {projects.map((project) => (
                <ListItem key={project.id} disablePadding sx={{ mb: 0 }}>
                  <Paper
                    sx={{
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '10px',
                      border: '1px solid #333',
                      boxShadow: 'none',
                      width: '100%'
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleLoadProject(project)}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#3d3d3d' }
                      }}
                    >
                      <ListItemText
                        primary={project.name}
                        secondary={project.type || ''}
                        primaryTypographyProps={{ style: { color: '#ffffff' } }}
                        secondaryTypographyProps={{ style: { color: '#aaaaaa' } }}
                      />
                    </ListItemButton>

                    <IconButton
                      onClick={(e) => handleOpenMenu(e, project.id)}
                      sx={{
                        color: '#ffffff',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s',
                        '&:hover': { backgroundColor: '#333333' }
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor.anchor}
                      open={menuAnchor.projectId === project.id}
                      onClose={handleCloseMenu}
                      PaperProps={{
                        sx: {
                          backgroundColor: '#2a2a2a',
                          color: '#ffffff',
                          borderRadius: '8px',
                          boxShadow: 'none',
                          border: '1px solid #333'
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => handleDeleteProject(project.id)}
                        sx={{
                          '&:hover': { backgroundColor: '#444444' }
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </Paper>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectManager;
